/** Wall-clock time range (ISO-8601 strings) a clip covers. */
export type ClipTimeRange = {
  startTime: string
  endTime: string
}

export interface IncidentVideoStateIProps {
  url: string
  isLoading: boolean
  errorMessage?: string
  isProcessing?: boolean
  /** First available EXTENDED (forensic) clip url, when present. Powers the "Extended" toggle. */
  extendedUrl?: string | null
  /**
   * True while the hook is actively polling for the EXTENDED clip (it lands minutes after the
   * incident); false once it arrives or the polling window closes. Drives the Extended tab's
   * loading state.
   */
  isExtendedClipPending?: boolean
  /**
   * Wall-clock range of the resolved (focused) clip, present only when it came from
   * `Incident.clips` (null when falling back to `Incident.video`, which carries no times).
   */
  resolvedClipRange?: ClipTimeRange | null
  /** Wall-clock range of the first EXTENDED (forensic) clip, when present. */
  extendedClipRange?: ClipTimeRange | null
  /**
   * Terminal failure of the resolved (focused) clip: it never became playable within its window,
   * measured from the incident's creation. Drives the Focused tab's error state — the loading
   * state must never be able to outlive this.
   */
  hasFocusedClipFailed?: boolean
  /**
   * Terminal failure of the EXTENDED (forensic) clip, once the focused clip has loaded. Drives the
   * Extended tab's error state.
   */
  hasExtendedClipFailed?: boolean
}

export interface DeviceFeedIProps {
  isDeviceDataLoading?: boolean
  stream: CameraStreamStateIProps
  nextCamera: () => void
  previousCamera: () => void
  availableCamerasIds: string[]
  currentCameraId?: string
  errorMessage?: string
}

/**
 * The live stream a camera feed resolved to, discriminated on the
 * `streamingUrlWrapper` GraphQL variant (`__typename`):
 * - `hls` — the signed expiring URL every VMS returns today
 *   (`StreamingUrlWrapperHls`), played by the shared `VideoPlayer`.
 * - `livekit` — Lumana cloud WebRTC (`StreamingUrlWrapperLiveKit`): the
 *   backend brokers a one-shot LiveKit grant and the browser connects
 *   directly with `Room.connect(apiUrl, token)` in `LiveKitVideoPlayer`.
 *   See ENG-31630 (decision) / ENG-31640 (player) / ENG-31639 (broker).
 */
export type CameraStream =
  | { kind: 'hls'; url: string }
  | { kind: 'livekit'; apiUrl: string; token: string }

export interface CameraStreamStateIProps {
  url: string
  isLoading: boolean
  errorMessage?: string
  // A safe refetch that swallows rejections (see useCameraFeed), so callers can
  // fire it from an onClick without risking an unhandled promise rejection.
  refetch?: () => void
  /**
   * Discriminated stream for kind-aware consumers (CameraDeviceDrawer).
   * `url` above stays the HLS url (empty for LiveKit streams) so the many
   * url-driven consumers keep working unchanged.
   */
  stream?: CameraStream | null
}

export interface VideoDimensionsIProps {
  width: number
  height: number
}

// Enums
export enum IncidentPlayerError {
  NO_INCIDENT_CLIP = 'No incident clip attached',
  UNABLE_TO_FIND = 'Unable to find clip at this time',
  ERROR_FETCHING_INCIDENT_CLIP = 'Error fetching incident clip',
  ERROR_FETCHING_STREAM = 'Error fetching live stream',
  NO_VIDEO_FEED = 'Live stream not available',
}

export enum VideoFeedEvent {
  BeginRequesting,
  Received,
  Playing,
  // A feed that was requested successfully but never played. Pairs with
  // `Playing` so a live-view success rate has a denominator.
  Failed,
}

export enum VideoControlsAction {
  Play,
  Pause,
  Backward10,
  Forward10,
  Rewind,
  FastForward,
  Fullscreen,
}

export enum VideoFrameActionType {
  CAPTURE_IMAGE = 'CAPTURE_IMAGE',
  SET_REFRESH_TRUE = 'SET_REFRESH_TRUE',
  SET_REFRESH_FALSE = 'SET_REFRESH_FALSE',
  RESET = 'RESET',
}

export interface VideoFrameStateIProps {
  imageUrl: string
  videoDimensions: VideoDimensionsIProps
  refreshing?: boolean
  captureCount?: number
}

export type VideoFrameAction =
  | { type: VideoFrameActionType.CAPTURE_IMAGE; payload: VideoFrameStateIProps }
  | { type: VideoFrameActionType.SET_REFRESH_TRUE }
  | { type: VideoFrameActionType.SET_REFRESH_FALSE }
  | { type: VideoFrameActionType.RESET }
